import { forwardRef } from "react";
import { Cloud } from "lucide-react";

function formatDate(iso) {
  if (!iso) return new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

/* AZ-900 simulation certificate (1000x707) — inline styles for reliable PNG export. */
const Az900Certificate = forwardRef(function Az900Certificate({ cert }, ref) {
  const met = cert.benchmark_met;
  return (
    <div
      ref={ref}
      style={{
        width: 1000, height: 707, background: "#ffffff", position: "relative",
        fontFamily: '"Segoe UI", Helvetica, Arial, sans-serif', color: "#1a1f36", boxSizing: "border-box", padding: 28,
      }}
    >
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg,#eaf3fc 0%,#ffffff 45%)" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 10, background: "linear-gradient(90deg,#0078d4,#50e6ff)" }} />
      <div style={{ position: "absolute", inset: 28, border: "2px solid #0078d4" }} />
      <div style={{ position: "absolute", inset: 36, border: "1px solid #c7e0f4" }} />

      <div style={{ position: "relative", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "40px 78px 30px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 8, background: "#0078d4", display: "grid", placeItems: "center" }}>
            <Cloud color="#ffffff" size={24} />
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontWeight: 800, fontSize: 20, letterSpacing: 1, lineHeight: 1, color: "#0b3a66" }}>HMG GLOBAL CONSULTS</div>
            <div style={{ fontSize: 10, color: "#0078d4", letterSpacing: 4, textTransform: "uppercase", marginTop: 4 }}>Azure Training · USA</div>
          </div>
        </div>

        <div style={{ marginTop: 22, fontSize: 38, letterSpacing: 3, color: "#0b3a66", fontWeight: 800, textTransform: "uppercase" }}>
          Certificate of Achievement
        </div>
        <div style={{ fontSize: 13, letterSpacing: 6, color: "#0078d4", textTransform: "uppercase", marginTop: 2, fontWeight: 600 }}>
          AZ-900 Microsoft Azure Fundamentals · Exam Simulation
        </div>

        <div style={{ marginTop: 20, fontSize: 14, color: "#5b6478", fontStyle: "italic" }}>This certificate is proudly presented to</div>
        <div style={{ fontWeight: 800, fontSize: 46, lineHeight: 1.05, marginTop: 6, color: "#0b3a66" }}>{cert.student_name || "Student"}</div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "12px 0 14px" }}>
          <span style={{ width: 90, height: 1, background: "#0078d4" }} />
          <span style={{ width: 6, height: 6, background: "#0078d4", transform: "rotate(45deg)" }} />
          <span style={{ width: 90, height: 1, background: "#0078d4" }} />
        </div>

        <div style={{ fontSize: 15, color: "#3a4257", maxWidth: 720, lineHeight: 1.6 }}>
          for completing the <span style={{ fontWeight: 700, color: "#0b3a66" }}>{cert.program}</span> with a simulated score of
          <span style={{ fontWeight: 800, color: met ? "#107c10" : "#b00020" }}> {cert.simulated_score}/1000</span> against a benchmark of {cert.benchmark}
          {met ? " — benchmark met." : " — below benchmark."}
        </div>

        <div style={{ marginTop: 16 }}>
          <span style={{
            fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", padding: "6px 16px", borderRadius: 999,
            background: met ? "#dff6dd" : "#fde7e9", color: met ? "#107c10" : "#b00020",
          }}>
            {met ? "Benchmark Met" : "Keep Practicing"}
          </span>
        </div>

        <div style={{ marginTop: "auto", width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div style={{ textAlign: "center", minWidth: 200 }}>
            <div style={{ fontWeight: 700, fontSize: 16, borderBottom: "1.5px solid #0b3a66", paddingBottom: 4 }}>{formatDate(cert.completion_date)}</div>
            <div style={{ fontSize: 10, color: "#5b6478", marginTop: 6, letterSpacing: 2, textTransform: "uppercase" }}>Date of Issue</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 9, color: "#94a3b8" }}>{cert.certificate_id}</div>
            <div style={{ fontSize: 8, color: "#94a3b8", marginTop: 2, maxWidth: 300 }}>
              Not a Microsoft certification. Simulated assessment for training only.
            </div>
          </div>
          <div style={{ textAlign: "center", minWidth: 200 }}>
            <div style={{ fontWeight: 700, fontSize: 16, borderBottom: "1.5px solid #0b3a66", paddingBottom: 4, fontStyle: "italic" }}>{cert.issuer}</div>
            <div style={{ fontSize: 10, color: "#5b6478", marginTop: 6, letterSpacing: 2, textTransform: "uppercase" }}>Authorized Issuer</div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Az900Certificate;
